(ns app.front.view.workspace.subs
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.ecm-integration-model :as ecm-integration-model]
            [app.domain.group.model :as group-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.party.model :as party-model]
            [app.domain.property.model :as property-model]
            [app.domain.sign-off.model :as sign-off-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.view.group.subs :as group-subs]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [core.db.entity.util :as eu]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::view-data
  :-> :view/workspace)


(rf/reg-event-db ::workspace-fetch-fail
  (fn [db [_ _status _response]]
    ; TODO inspect information about error from response?
    (let [error :not-found]
      (update db :view/workspace assoc :fetch-status error))))


(rf/reg-event-db ::set-fetch-status
  (rf/path [:view/workspace])
  (fn [view [_ status]]
    (assoc view :fetch-status status)))


(rf/reg-sub ::fetch-status
  :<- [::view-data]
  (fn [view-data _]
    (-> view-data :fetch-status)))

;; loading the workspace view?
(rf/reg-sub ::loading?
  :<- [::fetch-status]
  (fn [fetch-status _]
    (or (nil? fetch-status) (= :fetching fetch-status))))

;; loading configurations, e.g. party role names, attachment type names, review types etc
;; the page can be displayed before these are available
(rf/reg-sub ::loading-config?
  :<- [::group-id]
  (fn [group-id _]
    (and group-id ;; when not in workspace page context, return false
         (<sub [:app.front.view.workspace.get/fetching-config? group-id]))))

(rf/reg-sub ::not-found
  :<- [::fetch-status]
  (fn [fetch-status _]
    (= fetch-status :not-found)))

(rf/reg-sub ::omit-execution?
  :<- [::workspace]
  (fn [workspace _]
    (-> workspace config-model/resolve-omit-execution boolean)))

(rf/reg-sub ::application-id
  :<- [::view-data]
  :-> :application-id)

(rf/reg-sub ::workspace-id
  :<- [::view-data]
  (fn [view-data _]
    (-> view-data :workspace/id)))

(rf/reg-sub ::workspace-state
  :<- [::workspace]
  :-> :domain/state)

(rf/reg-sub ::required-so-roles
  :<- [::view-data]
  :<- [::party-roles]
  :<- [::config-tuple]
  (fn [[view-data party-roles configs] _]
    (let [required-roles-from-backend (:required-so-roles view-data)]
      (->> (or
             ;; After sign-off has been done, the required roles will no longer be changed
             ;; by changes to config. The backend sends the list, determined using `d/as-of`
             required-roles-from-backend
             ;; If backend did not send a list, the sign-off is pending and the roles can be
             ;; determined by looking at the config entities.
             (sign-off-model/required-sign-off-role-keys configs))
           (map #(p/find-first party-roles {:party-role/key %}))))))

(rf/reg-sub ::sign-off-declarations
  :<- [::view-data]
  :<- [::config-tuple]
  (fn [[view-data configs] _]
    (or
      ;; if backend provided the declarations, use them - they are a snapshot from when
      ;; the sign-off was actually done
      (-> view-data :sign-off-declarations)
      ;; otherwise, resolve from configs
      (config-model/resolve-sign-off-declarations configs))))

(rf/reg-sub ::sign-off-declaration-schema-for-role
  :<- [::sign-off-declarations]
  (fn [declarations [_ party-role]]
    (->> declarations
         (filter (fn [{declaration-role :declaration-config/party-role}]
                   (-> (if (int? declaration-role)
                         declaration-role
                         (:db/id declaration-role))
                       (= (:db/id party-role)))))
         first
         :declaration-config/data-schema)))

(rf/reg-sub ::payment-use-municipal-case-id?
  :<- [::view-data]
  (fn [view-data _]
    (-> view-data :payment-use-municipal-case-id boolean)))

(rf/reg-sub ::group-features
  :<- [::view-data]
  :<- [::authority-group]
  (fn [[data group] _]
    (set/union (:group/features group)
               (:group-features data))))

(rf/reg-sub ::has-feature?
  :<- [::group-features]
  (fn [features [_ feature]]
    (contains? features feature)))

(rf/reg-sub ::workspace
  :<- [::entities/context]
  :<- [::workspace-id]
  (fn [[ctx workspace-id] _]
    (when workspace-id (entities/find-entity ctx {:workspace/id workspace-id}))))

(rf/reg-sub ::customer-id
  :<- [::workspace]
  :-> workspace-model/customer-id)

(rf/reg-sub ::municipality-config
  :<- [:app.front.municipality/municipality-config]
  :<- [::customer-id]
  (fn [[configs customer-id] _]
    (get configs customer-id)))

(rf/reg-sub ::user-is-party-with-write-auths?
  :<- [::workspace]
  :<- [:session/user]
  (fn [[{:keys [granted-permissions] :as workspace} user] _]
    (and (contains? granted-permissions :permission.workspace/write)
         (->> workspace
              workspace-model/workspace-target
              party-model/phase-parties
              (filter (partial party-model/user-can-act-as-party? user))
              seq))))

(rf/reg-sub ::user-has-permission?
  :<- [::workspace]
  (fn [{:keys [granted-permissions]} [_ permission]]
    (contains? granted-permissions permission)))

(rf/reg-sub ::workspace-entity-id
  :<- [::workspace]
  :-> :db/id)

(rf/reg-sub ::authority-group
  :<- [::workspace]
  :-> :domain/authority-group)

(rf/reg-sub ::authority-group-id
  :<- [::authority-group]
  :-> :db/id)

(rf/reg-sub ::permit-type-name
  :<- [::workspace]
  :<- [:language]
  :<- [:tr]
  (fn [[workspace lang tr] _]
    (let [permit-type (:workspace/permit-type workspace)]
      (or (-> workspace :workspace/permit-type-group-config :group.config/names
              (i18n-model/get-text-from-entities-or-map lang))
          (tr [permit-type])))))

(rf/reg-sub ::backend-system-name
  :<- [::workspace]
  :<- [:tr]
  (fn [[workspace tr]]
    (if-let [group-id (-> workspace :domain/authority-group :group/id)]
      (group-model/backend-system-name tr group-id)
      "")))

(rf/reg-sub ::ecm-system-name
  :<- [::authority-group]
  :<- [:tr]
  (fn [[group tr]]
    (ecm-integration-model/system-name tr group)))

(rf/reg-sub ::ecm-folder-link
  :<- [::workspace]
  :-> ecm-integration-model/folder-link)

(rf/reg-sub ::group-time-zone
  :<- [::authority-group]
  (fn [group]
    (-> group :group/tz)))

(rf/reg-sub ::entities-for-group
  :<- [::authority-group-id]
  :<- [::entities/context]
  group-subs/handle-entities-for-group)


(rf/reg-sub ::applicable-party-role-ids
  :<- [::config-tuple]
  (fn [group-configs _]
    (->> group-configs config-model/resolve-applicable-roles-for-target du/entity-id-set)))

(rf/reg-sub ::closure-categories
  :<- [::config-tuple]
  (fn [group-configs _]
    (->> group-configs
         (mapcat :group.config/close-without-inspection-categories))))

(rf/reg-sub ::party-roles
  :<- [::authority-group-id]
  (fn [auth-group-id]
    @(rf/subscribe [::group-subs/available-party-roles auth-group-id])))

(rf/reg-sub ::sorted-party-roles
  (fn [_]
    [(rf/subscribe [:language])
     (rf/subscribe [::party-roles])
     (rf/subscribe [::applicable-party-role-ids])])
  (fn [[lang party-roles applicable-role-ids] _]
    (->> applicable-role-ids
         (map #(p/find-first party-roles {:db/id %}))
         (sort-by #(role-model/party-role-name % lang)))))

(rf/reg-sub ::allowed-locales
  :<- [::authority-group]
  i18n/group-locales-handler)

(rf/reg-sub ::neighbour-ids
  :<- [::workspace]
  :<- [:app.front.view.workspace.neighbour-properties/reset-in-progress?]
  :<- [::ff/enabled? :feature-flag.id/property-data-integrity-tooling]
  (fn [[workspace reset-in-progress? property-id-lookup?] _]
    (when (and (:workspace/neighbour-properties-set? workspace)
               (not (get reset-in-progress? (:db/id workspace))))
      (workspace-model/neighbour-ids workspace property-id-lookup?))))

(rf/reg-sub ::neighbour-map-ids
  :<- [::workspace]
  :<- [:app.front.view.workspace.neighbour-properties/reset-in-progress?]
  :<- [::workspace-subdivision]
  :<- [::country]
  :<- [::ff/enabled? :feature-flag.id/property-data-integrity-tooling]
  (fn [[workspace reset-in-progress? subdivision country property-id-lookup?] _]
    (when (and (:workspace/neighbour-properties-set? workspace)
               (not (get reset-in-progress? (:db/id workspace))))
      (workspace-model/neighbour-map-ids workspace property-id-lookup? (or subdivision country)))))

(rf/reg-sub ::neighbours
  :<- [::workspace]
  (fn [ws [_ selected]]
    (->> ws
         :workspace/neighbour-properties
         ;; sort selected property as first in the list
         (sort (fn [f s]
                 (cond (= (:property/integration-id s) selected) 1
                       (= (:property/integration-id f) selected) -1
                       :else (compare (:property/integration-id f) (:property/integration-id s))))))))

(defn ws-child-signal [_]
  [(rf/subscribe [::entities/context])
   (rf/subscribe [::workspace])])

(defn ws-child-handler-fn [entity-type]
  (fn [[ctx workspace] _]
    (entities/find-entity ctx {:entity/parent (:db/id workspace)
                               :entity/type   entity-type})))

(defn ws-children-handler-fn [entity-type]
  (fn [[ctx workspace] _]
    (entities/find-entities ctx {:entity/parent (:db/id workspace)
                                 :entity/type   entity-type})))

(rf/reg-sub ::pre-consultation
  ws-child-signal
  (ws-child-handler-fn :entity.type/pre-consultation))

(rf/reg-sub ::prior-pre-consultation
  :<- [::workspace]
  :-> :workspace/prior-pre-consultation)

(rf/reg-sub ::own-or-prior-pre-consultation
  :<- [::pre-consultation]
  :<- [::prior-pre-consultation]
  (fn [[pre-con prior-pre-con] _]
    (or pre-con prior-pre-con)))

(rf/reg-sub ::default-approver-groups
  :<- [::workspace]
  (fn [workspace _]
    (->> workspace
         workspace-model/default-config-path
         config-model/default-approver-groups
         (map :db/id))))

(rf/reg-sub ::application
  ws-child-signal
  (ws-child-handler-fn :entity.type/application))

(rf/reg-sub ::execution
  ws-child-signal
  (ws-child-handler-fn :entity.type/execution))

(rf/reg-sub ::periodic-parent
  ws-child-signal
  (ws-child-handler-fn :entity.type/periodic-parent))

(rf/reg-sub ::archiving
  ws-child-signal
  (ws-child-handler-fn :entity.type/archiving))

(rf/reg-sub ::permit-type
  :<- [::view-data]
  :-> :permit-type)

(rf/reg-sub ::group-id
  :<- [::view-data]
  :-> :group/id)

(rf/reg-sub ::instructions-text-for-the-phase
  :<- [::view-data]
  (fn [data [_ entity-type-of-the-phase]]
    (get-in data [:instr-text-for-phases entity-type-of-the-phase])))

(rf/reg-sub ::public-notice-contacts
  ws-child-signal
  (ws-children-handler-fn :entity.type/public-notice-contact))

(rf/reg-sub ::last-state-change-date
  :<- [::view-data]
  (fn [view-data _]
    (or (:last-state-change-date view-data)
        ;; Provide a fallback value in case the workspace transitions
        ;; from pre-consultation to application without refreshing the
        ;; workspace entity
        (js/Date.))))

(rf/reg-sub ::public-notice-published?
  :<- [::workspace]
  :-> :workspace/public-notice-published?)

(rf/reg-sub ::tr-replacements
  :<- [::authority-group]
  :<- [::permit-type]
  (fn [[authority-group permit-type] _]
    (merge (group-model/get-i18n-mapping authority-group)
           (i18n-model/permit-type-specific-keys permit-type))))

(rf/reg-sub ::tr
  :<- [::tr-replacements]
  :<- [:tr]
  (fn [[replacements tr] _]
    ;; Note that although workspace-model/workspace-tr produces
    ;; the same end result, we want to control the updates and
    ;; therefore take the minimum inputs here instead of just
    ;; the workspace entity
    (i18n-model/wrap-tr-fn replacements tr)))

(rf/reg-sub ::tr-quiet
  :<- [::tr-replacements]
  :<- [:tr/quiet]
  (fn [[replacements tr] _]
    (i18n-model/wrap-tr-fn replacements tr)))

(rf/reg-sub ::workspace-municipality
  :<- [::workspace]
  :-> :workspace/municipality)

(rf/reg-sub ::workspace-subdivision
  :<- [::workspace-municipality]
  :-> :municipality/subdivision)

(rf/reg-sub ::country
  :<- [::workspace-municipality] :-> :municipality/country)

(rf/reg-sub ::property-attr-required?
  :<- [::workspace-subdivision]
  :<- [::permit-type]
  (fn [[subdivision permit-type] [_ attr]]
    (contains? (property-model/required-property-keys subdivision permit-type)
               attr)))

(rf/reg-sub ::property-attributes-with-templates
  :<- [::workspace]
  (fn [workspace]
    (->> workspace
         config-model/workspace-primary-property-attributes
         (sort-by (comp :ordered/order
                        :property-attribute/template)))))

(rf/reg-sub ::category-group-config
  :<- [::workspace]
  :-> :workspace/category-group-config)

(rf/reg-sub ::additional-categories
  :<- [::workspace]
  :-> :workspace/additional-categories)

(rf/reg-sub ::process-flow
  :<- [::workspace]
  (fn [ws _]
    (config-model/resolve-value :group.config/process-flow ws)))

(rf/reg-sub ::config-tuple
  :<- [::workspace]
  :-> workspace-model/default-config-path)

(rf/reg-sub ::show-complaint-severity?
  :<- [::config-tuple]
  (fn [config-tuple _]
    (let [permit-type-gc (first config-tuple)]
      (and (ff/enabled? :feature-flag.id/complaints-priority)
           (#{:group-config.complaint-severity-use/required
              :group-config.complaint-severity-use/optional}
            (:group.config/complaint-severity-use permit-type-gc))))))

(rf/reg-sub ::complaints
  :<- [::workspace]
  (fn [ws _]
    (->> ws
         :entity/_parent
         (filter (eu/is-type-fn :entity.type/complaint)))))

(rf/reg-sub ::workspace-target
  :<- [::workspace]
  :-> workspace-model/workspace-target)

(rf/reg-sub ::href
  :<- [::workspace]
  :-> ws-util/workspace-href)

(rf/reg-sub ::enabled-attachment-type-ids
  :<- [::view-data]
  :-> :attachment-types)

(rf/reg-sub ::domain-group
  :<- [::authority-group]
  :-> :entity/parent)

(rf/reg-sub ::property-unit-designators
  :<- [::domain-group]
  :<- [:language]
  (fn [[domain-group lang] _]
    (->> (:property-unit-designator/_group domain-group)
         (filter (du/in-state-fn :property-unit-designator.state/active))
         (sort-by #(property-model/unit-designator-name % lang)))))
